import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/OrderStatus.css';

interface User {
  _id: string;
  name: string;
  email: string;
}

interface Address {
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  stateProvinceRegion: string;
  postalCode: string;
  country: string;
}

interface ContactForm {
  _id: string;
  userId: string;
  date: string;
  status: string;
  content: string;
  extraInfo?: string;
  totalPrice: number;
  address: Address;
  user?: User; // We'll add this when combining data
}

const OrderStatus: React.FC = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState<ContactForm[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<ContactForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const statuses = [
    'Quote Sent',
    'Order Confirmed',
    'Invoice Issued',
    'Payment Pending',
    'Payment Received',
    'Preparing for Shipment',
    'In Transit',
    'Delivered'
  ];

  // Fetch users and orders
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        
        // Fetch users
        const usersResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/users/all`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!usersResponse.ok) {
          throw new Error(`Error fetching users: ${usersResponse.status}`);
        }
        
        const usersData = await usersResponse.json();
        setUsers(usersData);
        
        // Fetch orders
        const ordersResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/contact-forms/all`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!ordersResponse.ok) {
          throw new Error(`Error fetching orders: ${ordersResponse.status}`);
        }
        
        const ordersData = await ordersResponse.json();
        
        // Combine orders with user data
        const ordersWithUsers = ordersData.map((order: ContactForm) => ({
          ...order,
          user: usersData.find((user: User) => user._id === order.userId)
        }));
        
        setOrders(ordersWithUsers);
        setFilteredOrders(ordersWithUsers);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Filter orders based on search query and status
  useEffect(() => {
    let result = [...orders];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order => 
        order.user?.name.toLowerCase().includes(query) ||
        order.user?.email.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter) {
      result = result.filter(order => order.status === statusFilter);
    }
    
    setFilteredOrders(result);
  }, [orders, searchQuery, statusFilter]);

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    if (!token) return;
    
    setUpdatingOrderId(orderId);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact-forms/${orderId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      
      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      );
      
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Failed to update order status. Please try again.');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  return (
    <div className="order-status-container">
      <div className="orders-header">
        <div className="header-row">
          <h1 className="page-title">Order Management</h1>
          
          <div className="search-controls">
            <div className="search-field">
              <input
                type="text"
                placeholder="Search by name or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>
            
            <div className="status-filter-wrapper">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-filter"
              >
                <option value="">All Statuses</option>
                {statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="orders-list">
        {loading ? (
          <div className="loading-message">Loading orders...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filteredOrders.length === 0 ? (
          <div className="no-orders">
            <p>No orders found matching your criteria.</p>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div className="order-info">
                  <span className="order-date">
                    {new Date(order.date).toLocaleDateString()}
                  </span>
                  <span className="order-price">
                    {order.totalPrice.toFixed(2)} €
                  </span>
                  {order.user && (
                    <span className="order-user">
                      <span className="user-avatar">
                        {order.user.name.charAt(0).toUpperCase()}
                      </span>
                      <span className="user-name">{order.user.name}</span>
                      <span className="user-email">{order.user.email}</span>
                    </span>
                  )}
                </div>
                
                <select
                  value={order.status}
                  onChange={(e) => handleStatusChange(order._id, e.target.value)}
                  disabled={updatingOrderId === order._id}
                  className={`status-select ${order.status.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div className="order-content">
                <strong>Order Content:</strong> {order.content}
              </div>
              
              {order.extraInfo && (
                <div className="extra-info">
                  <strong>Additional Information:</strong> {order.extraInfo}
                </div>
              )}
              
              <div className="order-address">
                <strong>Shipping Address:</strong>
                {order.address.streetAddress1}
                {order.address.streetAddress2 && <>, {order.address.streetAddress2}</>}<br />
                {order.address.city}, {order.address.stateProvinceRegion} <span className="postal-code">{order.address.postalCode.toUpperCase()}</span><br />
                {order.address.country}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrderStatus; 